import React, { useRef } from 'react';
import gql from 'graphql-tag';
import { put } from '@sporkbytes/api-client/Fees';
import { Alert, Currency, Percent } from '@sporkbytes/material-ui-kit-react';

import ActionMenu from 'components/utilities/ActionMenu';
import BooleanFieldSwitch from 'components/utilities/BooleanFieldSwitch';
import Page from 'components/layout/Page';
import PageHeader from 'components/content/PageHeader';
import RemoteTable from 'components/content/RemoteTable';

import { useStore } from 'models/store';

import { useAuth } from 'services/auth';
import { createEditAction } from 'services/actionMenu';

const getFeesQuery = searchTerm =>
	gql`
		query Fees(
			$activeOrderBy: order_by
			$amountOrderBy: order_by
			$descriptionOrderBy: order_by
			$nameOrderBy: order_by
			$limit: Int!
			$offset: Int!
		) {
			Fees(
				where: {
					name: { _ilike: "%${searchTerm}%" }
					SporkLocation: { active: { _eq: true } }
				}
				order_by: {
					active: $activeOrderBy
					amount: $amountOrderBy
					description: $descriptionOrderBy
					name: $nameOrderBy
				}
				limit: $limit
				offset: $offset
			) {
				id
				active
				amount
				description
				name
				type
				SporkLocation {
					name
				}
			}
			Fees_aggregate(
				where: {
					name: { _ilike: "%${searchTerm}%" }
					SporkLocation: { active: { _eq: true } }
				}
			) {
				aggregate {
					count
				}
			}
		}
	`;

const Fees = () => {
	const { userCan } = useAuth();
	const ref = useRef();
	const entityType = 'Fees';
	const { hasMoreThanOneSporkLocation } = useStore();

	return (
		<Page title={entityType}>
			<PageHeader headerText={entityType} />
			<Alert type="info">
				These Fees are added to a Meal Proposal by default. Once added
				to a proposal, they can be modified per proposal. You can always
				add an ad hoc fee to a specific proposal as well.
			</Alert>
			<RemoteTable
				tableRef={ref}
				columns={[
					{
						title: 'Name',
						field: 'name',
					},
					{
						title: 'Amount',
						field: 'amount',
						render: ({ amount, type }) =>
							type === 'percent' ? (
								<Percent value={amount} />
							) : (
								<Currency value={amount} />
							),
					},
					{
						title: 'Type',
						field: 'type',
						sorting: false,
					},
					{
						title: 'Description',
						field: 'description',
					},
					{
						title: 'Active',
						field: 'active',
						render: ({ active, id }) => (
							<BooleanFieldSwitch
								afterChange={ref.current.onQueryChange}
								entityId={id}
								entityType="fees"
								updateEntity={put}
								value={active}
							/>
						),
					},
					{
						title: 'Spork Location',
						field: 'SporkLocation.name',
						sorting: false,
						hidden: !hasMoreThanOneSporkLocation(),
					},
					{
						title: 'Actions',
						field: 'actions',
						sorting: false,
						render: ({ id, name }) => (
							<ActionMenu
								options={[
									createEditAction({
										entityId: id,
										entityName: name,
										entityType,
										userCan,
									}),
								]}
							/>
						),
					},
				]}
				getQuery={getFeesQuery}
				defaultOrderBy="name"
				defaultOrderDirection="asc"
				queryEntity="Fees"
				addNewEntityRouteName="feesCreate"
				entityToAddName="Fee"
				canAddNewEntity={userCan(['fees:create'])}
			/>
		</Page>
	);
};

export default Fees;
